import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, LogOut, LayoutDashboard, User, Ticket, ScanLine, Bell, Sun, Moon, ChevronDown, Briefcase } from "lucide-react";
import EventomLogo from "@/components/EventomLogo";
import InstallAppButton from "@/components/InstallAppButton";
import FanNotificationBell from "@/components/FanNotificationBell";
import { CitySelector } from "@/context/CityContext";
import { useTheme } from "@/context/ThemeContext";
import { api } from "@/lib/api";

// Nav — Home · Events · Contests · AI Photo Gallery · Club VIP · Shop · Grow Your Business
const fanLinks = [
  { to: "/", label: "Home", testId: "nav-home" },
  { to: "/events", label: "Events", testId: "nav-events" },
  { to: "/contests", label: "Contests", testId: "nav-contests" },
  { to: "/ai-gallery", label: "AI Photo Gallery", testId: "nav-ai-gallery" },
  { to: "/clubvip", label: "Club VIP", testId: "nav-clubvip" },
  { to: "/shop", label: "Shop", testId: "nav-shop" },
  { to: "/ecosystem", label: "Grow Your Business", testId: "nav-grow-biz" },
];

// Business dropdown restored (Iter 40 — user asked us to bring these back).
// Kept as a subtle secondary group so it doesn't crowd the primary 6.
const businessLinks = [
  { to: "/organisers", label: "For Organisers", testId: "nav-biz-organisers" },
  { to: "/organizer/signup", label: "List Your Event", testId: "nav-biz-list-event" },
  { to: "/brands", label: "For Brands", testId: "nav-biz-brands" },
  { to: "/blog", label: "Blog", testId: "nav-biz-blog" },
  { to: "/work-with-eventom", label: "Work With Us", testId: "nav-biz-work" },
  { to: "/about", label: "About", testId: "nav-biz-about" },
];

export const Logo = () => {
  const { theme } = useTheme();
  return (
    <Link to="/" data-testid="logo-link" className="inline-flex items-center">
      <EventomLogo height={32} variant={theme === "light" ? "light" : "dark"} />
    </Link>
  );
};

const BUCKETS = [
  { key: "vip", label: "VIP applications", tab: "vip", color: "text-magenta" },
  { key: "vip_requests", label: "VIP after-party requests", tab: "vip", color: "text-purple" },
  { key: "ecosystem", label: "Ecosystem listings", tab: "partners", color: "text-lime" },
  { key: "brands", label: "Brand enquiries", tab: "partners", color: "text-skyx" },
  { key: "clubvip", label: "ClubVIP profile verifications", tab: "clubvip", color: "text-pink" },
  { key: "kyc", label: "Organiser KYC", tab: "kyc", color: "text-orangex" },
];

function ApprovalBell() {
  const [data, setData] = useState({ total: 0, buckets: {} });
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const nav = useNavigate();
  useEffect(() => {
    const load = () => api.get("/admin/pending-counts").then(({ data }) => setData(data)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => { clearInterval(t); document.removeEventListener("mousedown", onDown); };
  }, []);
  const total = data.total || 0;
  const goto = (tab) => { setOpen(false); nav(`/admin?tab=${tab}`); window.dispatchEvent(new CustomEvent("admin-open-tab", { detail: tab })); };
  return (
    <div className="relative" ref={ref} data-testid="approval-bell">
      <button onClick={() => setOpen((v) => !v)} data-testid="approval-bell-btn" title="Pending approvals"
        className="relative inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/5 border border-white/15 hover:border-lime/40 transition-colors">
        <Bell className="w-4 h-4 text-white" />
        {total > 0 && (
          <span data-testid="approval-bell-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-magenta text-white text-[10px] font-bold flex items-center justify-center border-2 border-bg">{total > 99 ? "99+" : total}</span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-3 w-80 glass rounded-2xl overflow-hidden shadow-2xl z-50" data-testid="approval-bell-panel">
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
            <div>
              <div className="font-heading font-bold">Pending approvals</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted">{total} waiting for you</div>
            </div>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {total === 0 ? (
              <div className="p-6 text-center text-xs font-mono text-muted">Inbox zero. You're caught up ✓</div>
            ) : BUCKETS.map((b) => {
              const c = data.buckets?.[b.key] || 0;
              if (c === 0) return null;
              return (
                <button key={b.key} onClick={() => goto(b.tab)} data-testid={`bell-bucket-${b.key}`}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 text-left">
                  <span className={`font-mono font-bold text-lg ${b.color}`}>{c}</span>
                  <span className="flex-1 text-sm">{b.label}</span>
                  <span className="text-[10px] font-mono text-muted">Open →</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Nav() {
  const { user, logout } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(false);
  const [bizOpen, setBizOpen] = useState(false);
  const bizRef = useRef(null);

  useEffect(() => {
    const onDown = (e) => { if (bizRef.current && !bizRef.current.contains(e.target)) setBizOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const handleLogout = async () => { await logout(); setMenu(false); nav("/"); };

  const ThemeToggle = ({ compact }) => (
    <button onClick={toggleTheme} data-testid="theme-toggle" title={theme === "dark" ? "Switch to light" : "Switch to dark"}
      className={`inline-flex items-center justify-center rounded-full border border-white/15 bg-white/5 hover:border-lime/40 transition-colors ${compact ? "w-8 h-8" : "w-10 h-10"}`}>
      {theme === "dark" ? <Sun className={compact ? "w-3.5 h-3.5" : "w-4 h-4"} /> : <Moon className={compact ? "w-3.5 h-3.5" : "w-4 h-4"} />}
    </button>
  );

  return (
    <header className="sticky top-0 z-50 nav-glass text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Logo />
        <nav className="hidden lg:flex items-center gap-1">
          {fanLinks.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={l.testId} end={l.to === "/"}
              className={({ isActive }) =>
                `relative px-3 py-2 font-body font-bold text-[13px] uppercase tracking-widest transition-colors ${
                  isActive ? "text-lime" : "text-white/80 hover:text-white"
                } after:content-[''] after:absolute after:left-3 after:right-3 after:-bottom-0.5 after:h-[3px] ${
                  isActive ? "after:bg-lime" : "after:bg-transparent"
                }`
              }>
              {l.label}
            </NavLink>
          ))}
          <div className="relative" ref={bizRef}>
            <button data-testid="nav-business-toggle" onClick={() => setBizOpen((v) => !v)}
              className={`relative inline-flex items-center gap-1 px-3 py-2 font-body font-bold text-[13px] uppercase tracking-widest transition-colors ${bizOpen ? "text-magenta" : "text-white/60 hover:text-white"}`}>
              <Briefcase className="w-3.5 h-3.5" /> Business <ChevronDown className={`w-3.5 h-3.5 transition-transform ${bizOpen ? "rotate-180" : ""}`} />
            </button>
            {bizOpen && (
              <div className="absolute right-0 top-full mt-3 w-72 glass rounded-2xl overflow-hidden shadow-2xl z-50" data-testid="nav-business-menu">
                <div className="px-4 py-2.5 border-b border-white/10">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-magenta">Business with Eventom</div>
                  <div className="text-[10px] text-muted mt-0.5">Organisers · Brands · Careers</div>
                </div>
                {businessLinks.map((l) => (
                  <NavLink key={l.to} to={l.to} data-testid={l.testId} onClick={() => setBizOpen(false)}
                    className={({ isActive }) => `block px-4 py-2.5 text-sm hover:bg-white/5 border-l-2 ${isActive ? "border-magenta text-magenta bg-white/5" : "border-transparent text-white/85"}`}>
                    {l.label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          {user?.role === "admin" && <ApprovalBell />}
          {user && user.role !== "admin" && <FanNotificationBell />}
          <ThemeToggle />
          <InstallAppButton />
          <CitySelector />
          {!user ? (
            <>
              <Link to="/login" data-testid="nav-login-btn" className="btn-ghost !py-2 !px-5 !text-sm">Login</Link>
              <Link to="/signup" data-testid="nav-signup-btn" className="btn-primary !py-2 !px-5 !text-sm">Sign Up</Link>
            </>
          ) : (
            <div className="relative">
              <button data-testid="nav-user-menu" onClick={() => setMenu((v) => !v)}
                className="btn-lime !py-2 !px-4 !text-sm">
                <User className="w-4 h-4" /> {user.name?.split(" ")[0] || "You"}
              </button>
              {menu && (
                <div className="absolute right-0 top-full mt-3 w-60 glass rounded-2xl overflow-hidden shadow-2xl z-50">
                  <Link to="/profile" data-testid="menu-profile" onClick={() => setMenu(false)} className="flex items-center gap-2 px-4 py-3 hover:bg-white/5">
                    <Ticket className="w-4 h-4 text-lime" /> My Tickets
                  </Link>
                  {user.role === "organizer" && (
                    <Link to="/organizer" data-testid="menu-organizer" onClick={() => setMenu(false)} className="flex items-center gap-2 px-4 py-3 hover:bg-white/5">
                      <LayoutDashboard className="w-4 h-4 text-orangex" /> Organiser
                    </Link>
                  )}
                  {(user.role === "organizer" || user.role === "admin") && (
                    <Link to="/scanner" data-testid="menu-scanner" onClick={() => setMenu(false)} className="flex items-center gap-2 px-4 py-3 hover:bg-white/5">
                      <ScanLine className="w-4 h-4 text-lime" /> Scan Tickets
                    </Link>
                  )}
                  {user.role === "admin" && (
                    <Link to="/admin" data-testid="menu-admin" onClick={() => setMenu(false)} className="flex items-center gap-2 px-4 py-3 hover:bg-white/5">
                      <LayoutDashboard className="w-4 h-4 text-purplex" /> Admin
                    </Link>
                  )}
                  <button data-testid="menu-logout" onClick={handleLogout} className="w-full flex items-center gap-2 px-4 py-3 text-left border-t border-white/5 hover:bg-magenta/10 text-magenta">
                    <LogOut className="w-4 h-4" /> Logout
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex lg:hidden items-center gap-2">
          {user?.role === "admin" && <ApprovalBell />}
          {user && user.role !== "admin" && <FanNotificationBell />}
          <ThemeToggle compact />
          <InstallAppButton className="!px-2.5 !py-1.5 !text-[10px]" />
          <CitySelector />
          <button data-testid="nav-mobile-toggle" onClick={() => setOpen((v) => !v)} className="p-2 rounded-xl border border-white/20 bg-white/5 text-white">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-white/10 nav-glass text-white">
          <div className="px-4 py-4 flex flex-col gap-2">
            {fanLinks.map((l) => (
              <NavLink key={l.to} to={l.to} data-testid={`m-${l.testId}`} onClick={() => setOpen(false)} end={l.to === "/"}
                className={({ isActive }) => `px-4 py-3 rounded font-bold uppercase text-sm border-2 ${isActive ? "bg-lime text-ink border-lime" : "bg-white/5 border-white/10 text-white"}`}>
                {l.label}
              </NavLink>
            ))}
            <div className="text-[10px] font-mono uppercase tracking-widest text-magenta px-2 mt-3">Business with Eventom</div>
            {businessLinks.map((l) => (
              <NavLink key={l.to} to={l.to} data-testid={`m-${l.testId}`} onClick={() => setOpen(false)}
                className={({ isActive }) => `px-4 py-3 rounded font-bold uppercase text-xs border-2 ${isActive ? "bg-magenta text-white border-magenta" : "bg-white/5 border-white/10 text-white/85"}`}>
                {l.label}
              </NavLink>
            ))}
            {!user ? (
              <>
                <Link to="/login" onClick={() => setOpen(false)} data-testid="m-nav-login-btn" className="btn-ghost text-center justify-center">Login</Link>
                <Link to="/signup" onClick={() => setOpen(false)} data-testid="m-nav-signup-btn" className="btn-primary text-center justify-center">Sign Up</Link>
              </>
            ) : (
              <>
                <Link to="/profile" onClick={() => setOpen(false)} data-testid="m-menu-profile" className="btn-ghost text-center justify-center">Profile</Link>
                {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} data-testid="m-menu-admin" className="btn-lime text-center justify-center">Admin</Link>}
                {user.role === "organizer" && <Link to="/organizer" onClick={() => setOpen(false)} data-testid="m-menu-organizer" className="btn-lime text-center justify-center">Organiser</Link>}
                <button onClick={handleLogout} data-testid="m-menu-logout" className="btn-primary text-center justify-center">Logout</button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
